import { useState } from "react";
import type { ReactNode } from "react";
import { Heading } from "../atoms/Heading";
import { cn } from "../lib/cn";
import { AlertBanner } from "../molecules/AlertBanner";
import { ErrorState } from "../molecules/ErrorState";
import { Pagination } from "../molecules/Pagination";
import { SearchField } from "../molecules/SearchField";
import { Skeleton } from "../molecules/Skeleton";
import { BatchTable, type BatchTableProps } from "../organisms/BatchTable";

export interface BatchExplorerFilters {
	chainId?: string | null;
	anchored?: boolean | null;
}

export interface BatchExplorerPageProps {
	batches: BatchTableProps["batches"];
	pagination: { page: number; pageCount: number };
	onPageChange: (page: number) => void;
	filters: BatchExplorerFilters;
	onFilterChange: (filters: BatchExplorerFilters) => void;
	loading?: boolean;
	error?: Error | null;
	onSelectBatch: (root: string) => void;
	className?: string;
}

function BatchExplorerPage({
	batches,
	pagination,
	onPageChange,
	loading = false,
	error = null,
	onSelectBatch,
	className,
}: BatchExplorerPageProps): ReactNode {
	const [refFilter, setRefFilter] = useState("");

	const filtered =
		refFilter.trim() === ""
			? batches
			: batches.filter((b) =>
					b.merkleRoot.toLowerCase().includes(refFilter.trim().toLowerCase()),
				);

	const isFiltering = refFilter.trim() !== "";

	return (
		<div className={cn("flex flex-col gap-4", className)}>
			<Heading size="xl">Batches</Heading>

			<AlertBanner tone="info" title="What is this?">
				This is an advanced, technical view. A batch groups events from many
				different loans that were sealed together on the ledger. Most auditors
				won't need this — to check a specific loan or event, search for it from
				the top of any page.
			</AlertBanner>

			{error ? (
				<ErrorState error={error} title="Failed to load batches" />
			) : loading ? (
				<div className="flex flex-col gap-3">
					{Array.from({ length: 5 }).map((_, i) => {
						// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
						return <Skeleton key={i} className="h-14 w-full" />;
					})}
				</div>
			) : (
				<>
					<SearchField
						value={refFilter}
						onChange={setRefFilter}
						onClear={() => setRefFilter("")}
						placeholder="Find by technical reference"
					/>
					{isFiltering && (
						<p className="text-sm text-mutedForeground">
							Showing matches on this page
						</p>
					)}
					<BatchTable batches={filtered} onSelectBatch={onSelectBatch} />
					{!isFiltering && (
						<Pagination
							page={pagination.page}
							pageCount={pagination.pageCount}
							onPageChange={onPageChange}
							className="mt-2 justify-center"
						/>
					)}
				</>
			)}
		</div>
	);
}
BatchExplorerPage.displayName = "BatchExplorerPage";

export default BatchExplorerPage;
export { BatchExplorerPage };
